import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = {
  key: 1,
  class: "q-row products__promoted-product-container"
}
const _hoisted_3 = { class: "q-col-sm-12 products__promoted-product-container-inner" }
const _hoisted_4 = { class: "products__promoted-product q-p-1 q-position-relative q-d-flex q-flex-wrap" }
const _hoisted_5 = { class: "products__promoted-product-image-container q-col-4 q-col-lg-12 q-p-0" }
const _hoisted_6 = ["src", "alt"]
const _hoisted_7 = { class: "products__promoted-product-info q-col-8 q-col-lg-12 q-px-2 q-text-black q-text-start" }
const _hoisted_8 = { class: "products__promoted-product-title q-m-0 q-fw-bold" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = ["href"]
const _hoisted_11 = ["href"]

import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';
import { ref, inject, computed, watchEffect } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate } from '@/core/Template';
import type { SmartSearchConfigInterface, ProductInterface } from '@/core/types';
import { useSearchQueryStore } from '@/store/SearchQueryStore';
import { storeToRefs } from 'pinia';
import { highlightText } from '@/core/highlight';


export default /*@__PURE__*/_defineComponent({
  __name: 'PromotedProduct',
  props: {
    promotedProduct: {}
  },
  setup(__props: any) {

const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.topProduct.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;
const customTemplate = SmartSearchConfig?.value?.serp?.customComponents?.topProduct || null;

const props = __props;

// Źródła danych
const product = computed(() => props.promotedProduct ?? null);
const firstVariant = computed(() => product.value?.variants?.[0] ?? null);

const actualLink = ref<string>('');
const clickLink  = ref<string>('');

watchEffect(() => {
  actualLink.value = product.value?.link ?? '';
  clickLink.value  = product.value?.link_click ?? '';
});

const searchQueryStore = useSearchQueryStore();
const { searchQuery } = storeToRefs(searchQueryStore);

const highlightedTitle = computed(() =>
    highlightText(product.value?.title ?? '', searchQuery.value ?? '', 'products__promoted-product-title-highlight')
);

// Obraz
const staticImage = computed(() => {
  const imageLink = firstVariant.value?.image_link || 'https://static.quartic.com.pl/img/no-image/no-image.png';
  if (!useQuarticImages) return imageLink;
  return generateStaticImage(imageLink, width, height);
});

// Klik
const handleClick = () => {
  if (clickLink.value) actualLink.value = clickLink.value.replace(/^http:/, 'https:');
};

// --- Makra i custom template ---
const priceData = computed(() => {
  if (!firstVariant.value) return null;
  const customMapping = window._QONizer?.customSmartSearchConfig?.customPriceMapping;
  
  const priceKey = customMapping?.price || 'price';
  const oldPriceKey = customMapping?.oldPrice || 'old_price';
  const salePriceKey = customMapping?.salePrice || 'sale_price';

  const v = firstVariant.value;
  const rawPrice = v[priceKey];
  const rawOldPrice = v[oldPriceKey];
  const rawSalePrice = v[salePriceKey];

  const priceToParse = (typeof rawPrice === 'string' || typeof rawPrice === 'number') ? rawPrice : v.price;
  const oldPriceToParse = (typeof rawOldPrice === 'string' || typeof rawOldPrice === 'number') ? rawOldPrice : v.old_price;
  const salePriceToParse = (typeof rawSalePrice === 'string' || typeof rawSalePrice === 'number') ? rawSalePrice : v.sale_price;

  const secondPrice = (oldPriceToParse || salePriceToParse) as string | number | null;
  const parsedPrice = parsePrice(priceToParse as string | number | null);
  const parsedOldPrice = parsePrice(secondPrice);
  const realPrices = getRealPrice(parsedPrice, parsedOldPrice);
  
  return {
    price: realPrices.price,
    oldPrice: realPrices.oldPrice,
    parsedPrice,
    parsedOldPrice
  };
});

const omnibusPrice = computed(() => firstVariant.value ? parsePrice(firstVariant.value.omnibus_price) : null);

const discountValue = computed(() => {
  if (!priceData.value) return null;
  const { price, oldPrice, parsedPrice, parsedOldPrice } = priceData.value;
  if (parsedPrice && parsedOldPrice && parsedPrice >= parsedOldPrice) {
    return null;
  }
  return getDiscount(price, oldPrice);
});

const discount = computed(() => discountValue.value);

const formattedPrice = computed(() => priceData.value ? formatPrice(priceData.value.price, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');
const formattedPriceOld = computed(() => priceData.value ? formatPrice(priceData.value.oldPrice, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');
const formattedOmnibusPrice = computed(() => omnibusPrice.value !== null ? formatPrice(omnibusPrice.value, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');

const prepareClearLink = (link: string, clickLink: string) =>
    ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;

const generatedHtml = computed(() => {
  if (!customTemplate || !product.value || !firstVariant.value || !priceData.value) {
    return '';
  }

  // klon (żeby nie mutować oryginalnego wariantu z props)
  const v = { ...firstVariant.value };

  v.discount = discount.value ? `- ${discount.value}%` : null;
  v.image_link_cdn = staticImage.value;
  v.price_formatted = formattedPrice.value;
  v.price_number = priceData.value.price;
  v.old_price_formatted = formattedPriceOld.value;
  v.old_price_number = priceData.value.oldPrice;
  v.omnibus_price_formatted = formattedOmnibusPrice.value;
  v.omnibus_price_number = omnibusPrice.value;
  v.id = product.value!.id;
  v.link_click = clickLink.value;
  v.clear_link = prepareClearLink(actualLink.value, clickLink.value);

  addDisplayMacros(v);

  return createTemplate(customTemplate, v, [v]);
});

return (_ctx: any,_cache: any) => {
  return (_unref(customTemplate) && product.value && firstVariant.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        innerHTML: generatedHtml.value
      }, null, 8, _hoisted_1))
    : (product.value && firstVariant.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("img", {
                  class: "products__promoted-product-image q-p-1",
                  src: staticImage.value,
                  alt: product.value.title
                }, null, 8, _hoisted_6)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, [
                  _createElementVNode("span", { innerHTML: highlightedTitle.value }, null, 8, _hoisted_9)
                ]),
                _createVNode(AutocompletePriceBox, { product: firstVariant.value }, null, 8, ["product"]),
                _createElementVNode("a", {
                  href: actualLink.value,
                  onClick: handleClick,
                  class: "q-stretched-link"
                }, null, 8, _hoisted_10)
              ]),
              _createElementVNode("a", {
                href: actualLink.value,
                onClick: handleClick,
                class: "q-stretched-link"
              }, null, 8, _hoisted_11)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
}
}

})